import { cn } from '../utils.js';
import { cva, VariantProps } from 'class-variance-authority';
import { HTMLAttributes } from 'react';

export const textVariants = cva('', {
  variants: {
    color: {
      black: 'text-black',
      'gray-900': 'text-gray-900',
      'gray-800': 'text-gray-800',
      'gray-700': 'text-gray-700',
      'gray-600': 'text-gray-600',
      'gray-500': 'text-gray-500',
      'gray-300': 'text-gray-300',
      'gray-200': 'text-gray-200',
      link: 'text-link',
      white: 'text-white',
      red: 'text-red-400',
      'red-600': 'text-red-600',
      green: 'text-green-400',
      purple: 'text-secondary-purple',
      blue: 'text-secondary-blue',
      inherit: 'text-inherit',
      primaryOrange: 'text-primary-orange',
      'yellow-800': 'text-yellow-800',
      aqua: 'text-[#00f0ff]',
      mysteryCityRed: 'text-[#FB8C8C]',
      light1: 'text-light1',
    },
    font: {
      'light-lg': ['font-light', 'text-[24px]', 'leading-[32px]'],
      'light-md': ['font-light', 'text-[14px]', 'leading-[20px]'],
      'light-sm': ['font-light', 'text-[12px]', 'leading-[16px]'],
      'light-xs': ['font-light', 'text-[9px]', 'leading-[12px]'],

      'regular-3xl': ['font-light', 'text-[76px]', 'leading-[84px]'],
      '3xl': ['font-light', 'text-[76px]', 'leading-[84px]'],
      'regular-2xl': ['font-light', 'text-[42px]', 'leading-[48px]'],
      '2xl': ['font-light', 'text-[42px]', 'leading-[48px]'],
      'regular-xl': ['font-light', 'text-[32px]', 'leading-[40px]'],
      xl: ['font-light', 'text-[32px]', 'leading-[40px]'],
      'regular-lg': ['font-light', 'text-[24px]', 'leading-[32px]'],
      lg: ['font-light', 'text-[24px]', 'leading-[32px]'],
      'regular-md': ['font-light', 'text-[16px]', 'leading-[24px]'],
      md: ['font-light', 'text-[16px]', 'leading-[24px]'],
      'regular-sm': ['font-light', 'text-[14px]', 'leading-[20px]'],
      sm: ['font-light', 'text-[14px]', 'leading-[20px]'],
      'regular-xs': ['font-light', 'text-[12px]', 'leading-[16px]'],
      xs: ['font-light', 'text-[12px]', 'leading-[16px]'],
      xxs: ['font-light', 'text-[9px]', 'leading-[12px]'],

      'medium-2xl': ['font-normal', 'text-[48px]', 'leading-[56px]'],
      'medium-xl': ['font-normal', 'text-[32px]', 'leading-[40px]'],
      'medium-lg': ['font-normal', 'text-[24px]', 'leading-[32px]'],
      'medium-md': ['font-normal', 'text-[16px]', 'leading-[24px]'],
      'medium-sm': ['font-normal', 'text-[14px]', 'leading-[20px]'],
      'medium-xs': ['font-normal', 'text-[12px]', 'leading-[16px]'],
      'medium-xxs': ['font-normal', 'text-[10px]', 'leading-[14px]'],

      'bold-3xl': ['font-bold', 'text-[76px]', 'leading-[84px]'],
      'bold-2xl': ['font-bold', 'text-[42px]', 'leading-[48px]'],
      'bold-xl': ['font-bold', 'text-[32px]', 'leading-[40px]'],
      'bold-lg': ['font-bold', 'text-[24px]', 'leading-[32px]'],
      'bold-md': ['font-bold', 'text-[16px]', 'leading-[22px]'],
      'bold-sm': ['font-bold', 'text-[14px]', 'leading-[20px]'],
      'bold-xs': ['font-bold', 'text-[12px]', 'leading-[14px]'],

      'black-3xl': ['font-black', 'text-[76px]', 'leading-[84px]'],
      'black-2xl': ['font-black', 'text-[42px]', 'leading-[48px]'],
      'black-xl': ['font-black', 'text-[32px]', 'leading-[40px]'],
      'black-lg': ['font-black', 'text-[24px]', 'leading-[32px]'],
      'black-md': ['font-black', 'text-[18px]', 'leading-[24px]'],
    },
    family: {
      atak: 'font-body tracking-[0.02em]',
      itc: 'font-itc',
      space: 'font-space',
      arialRounded: 'font-arialRounded',
    },
  },
  defaultVariants: {
    color: 'black',
    font: 'regular-md',
    family: 'atak',
  },
});

export interface TextProps
  extends Omit<HTMLAttributes<HTMLParagraphElement>, 'color'>,
    VariantProps<typeof textVariants> {}

export const Text: React.FC<TextProps> = ({
  children,
  color,
  font,
  family,
  ...props
}: TextProps) => {
  return (
    <p className={cn(textVariants({ color, font, family }), props.className)}>
      {children}
    </p>
  );
};
